import React, { useState } from 'react';
import { signOut } from 'firebase/auth';
import { doc, setDoc, addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../firebase';

export default function TermsGate({ user, onAccepted }) {
  const [checked, setChecked]   = useState(false);
  const [loading, setLoading]   = useState(false);
  const [declining, setDeclining] = useState(false);

  const handleAccept = async () => {
    if (!checked) return;
    setLoading(true);
    try {
      await setDoc(doc(db, 'users', user.uid), {
        termsAccepted:   true,
        termsAcceptedAt: serverTimestamp(),
        termsVersion:    '1.0',
        email:           user.email,
      }, { merge: true });
      onAccepted();
    } catch (err) {
      console.error('Error saving terms acceptance:', err);
      setLoading(false);
    }
  };

  const handleDecline = async () => {
    setDeclining(true);
    try {
      await addDoc(collection(db, 'declined_terms'), {
        userId:     user.uid,
        email:      user.email,
        declinedAt: serverTimestamp(),
      });
    } catch (err) {
      console.error('Error logging decline:', err);
    } finally {
      signOut(auth);
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0,
      background: '#000',
      zIndex: 9999,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '24px',
    }}>
      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: '28px' }}>
        <div style={{ fontSize: '28px', fontWeight: '900', color: '#fff', letterSpacing: '4px' }}>
          ARCHX<span style={{ color: '#c8922a' }}>AI</span>
        </div>
        <div style={{ fontSize: '11px', color: '#c0c0c0', letterSpacing: '3px', marginTop: '4px' }}>
          FULL ARCH INTELLIGENCE
        </div>
      </div>

      {/* Modal box */}
      <div style={{
        background: '#111',
        border: '1px solid #333',
        borderRadius: '16px',
        maxWidth: '680px',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        maxHeight: '80vh',
        boxShadow: '0 32px 80px rgba(0,0,0,0.8)',
      }}>
        {/* Title bar */}
        <div style={{
          padding: '20px 28px 16px',
          borderBottom: '1px solid #222',
        }}>
          <div style={{ fontSize: '18px', fontWeight: '700', color: '#fff' }}>
            Terms & Conditions of Use
          </div>
          <div style={{ fontSize: '12px', color: '#888', marginTop: '4px' }}>
            Version 1.0 — Effective May 2026 — Please read carefully before continuing
          </div>
        </div>

        {/* Scrollable content */}
        <div style={{
          overflowY: 'auto',
          padding: '24px 28px',
          flex: 1,
          fontSize: '13px',
          color: '#ccc',
          lineHeight: '1.8',
        }}>

          <Section title="1. Agreement to Terms">
            By accessing or using the ARCHX AI clinician portal ("Portal"), you ("Clinician") agree
            to be bound by these Terms & Conditions ("Terms"). If you do not agree, you must decline
            and will not be granted access. These Terms constitute a legally binding agreement between
            you and ARCHX AI ("Company," "we," "us").
          </Section>

          <Section title="2. Eligibility & Authorized Use">
            The Portal is intended solely for use by licensed dental professionals and authorized
            dental laboratory personnel. By accepting these Terms, you represent and warrant that
            you are a licensed professional in good standing in your applicable jurisdiction and
            that you will use the Portal only for lawful, professional purposes consistent with
            applicable laws, regulations, and standards of care.
          </Section>

          <Section title="3. HIPAA & Patient Data">
            You are solely responsible for ensuring your use of the Portal complies with all
            applicable federal and state laws governing patient health information, including the
            Health Insurance Portability and Accountability Act (HIPAA) and any applicable state
            privacy laws. You agree not to upload any patient information beyond what is strictly
            necessary for case processing. ARCHX AI implements commercially reasonable security
            measures to protect data transmitted through the Portal but does not guarantee
            absolute security. You are responsible for obtaining all necessary patient consents
            before submitting case data.
          </Section>

          <Section title="4. Data Usage & AI Training">
            By submitting case data through the Portal, you voluntarily contribute de-identified
            scan data and prosthetic design outputs to the ARCHX AI training dataset. You
            understand and agree that these contributions are made without any expectation of
            compensation, royalty, or financial benefit of any kind. Scan submissions are
            provided as a voluntary contribution to the advancement of AI-driven dental
            technology, with the understanding that the collective benefit of improved AI
            accuracy serves the broader dental profession. ARCHX AI will not sell, share,
            or disclose personally identifiable patient information to third parties except
            as required by law. De-identified data may be retained indefinitely for model
            development and improvement purposes.
          </Section>

          <Section title="5. Clinical Responsibility & No Medical Advice">
            ARCHX AI provides AI-assisted design tools to support — not replace — the clinical
            judgment of licensed dental professionals. All outputs generated by the ARCHX AI
            system are design suggestions only and do not constitute medical advice, diagnosis,
            or treatment recommendations. The Clinician is solely and exclusively responsible
            for all clinical decisions, treatment plans, prosthetic designs, and patient
            outcomes. ARCHX AI expressly disclaims any liability for clinical decisions made
            in reliance on system outputs.
          </Section>

          <Section title="6. Intellectual Property">
            All ARCHX AI software, artificial intelligence models, algorithms, interfaces, and
            related technology are the exclusive intellectual property of ARCHX AI and are
            protected by applicable patent, copyright, trade secret, and other intellectual
            property laws. The Clinician retains ownership of uploaded scan data. ARCHX AI
            retains ownership of all generated design outputs, trained model weights, and
            derivative works. No license to ARCHX AI's intellectual property is granted
            beyond the limited right to use the Portal pursuant to these Terms.
          </Section>

          <Section title="7. Disclaimer of Warranties">
            The Portal and all outputs are provided "as is" and "as available" without
            warranty of any kind, express or implied, including but not limited to
            warranties of merchantability, fitness for a particular purpose, accuracy,
            or non-infringement. ARCHX AI does not warrant that the Portal will be
            error-free, uninterrupted, or free of harmful components.
          </Section>

          <Section title="8. Termination">
            ARCHX AI reserves the right to suspend or terminate your access to the Portal
            at any time, with or without cause, and with or without notice. Upon termination,
            your right to use the Portal ceases immediately. Provisions of these Terms that
            by their nature should survive termination shall survive, including Sections 11,
            12, and 12A.
          </Section>

          <Section title="9. Governing Law">
            These Terms shall be governed by and construed in accordance with the laws of the
            State of Nevada, without regard to its conflict of law provisions.
          </Section>

          <Section title="10. Updates to Terms">
            ARCHX AI reserves the right to update these Terms at any time. Material changes
            will require re-acceptance upon your next login. Continued use of the Portal
            following notice of changes constitutes acceptance of the updated Terms.
          </Section>

          <Section title="11. Mandatory Arbitration & Dispute Resolution">
            Any dispute, claim, or controversy arising out of or relating to these Terms
            or your use of the Portal shall be resolved exclusively by binding individual
            arbitration administered under the rules of the American Arbitration Association
            (AAA). You expressly waive any right to a jury trial and any right to
            participate in a class action, class arbitration, or representative proceeding.
            Arbitration shall take place in Clark County, Nevada. The arbitrator's decision
            shall be final and binding and may be entered as a judgment in any court of
            competent jurisdiction. Each party shall bear its own arbitration costs unless
            the arbitrator determines otherwise.
          </Section>

          <Section title="12. Limitation of Liability">
            To the maximum extent permitted by applicable law, ARCHX AI's total liability
            to you for any claim arising out of or related to your use of the Portal —
            including but not limited to AI-generated outputs, clinical decisions made
            in reliance on such outputs, data loss, service interruption, or any other
            matter related to the Portal or these Terms — shall be zero dollars ($0.00).
            ARCHX AI shall have no liability whatsoever for any indirect, incidental,
            special, consequential, or punitive damages of any kind, even if ARCHX AI
            has been advised of the possibility of such damages.
          </Section>

          <Section title="12A. Data Contribution Claims — Maximum Settlement">
            By submitting case data to the Portal, you acknowledge that your scan uploads
            constitute voluntary contributions to the ARCHX AI training dataset and that
            you are not an equity holder, co-developer, or co-inventor of the ARCHX AI
            system by virtue of such submissions. You agree that no compensation is owed
            for scan data submitted, and that such submissions carry no expectation of
            financial return. In the event of any future claim, dispute, or legal action
            in which you assert that your uploaded scan data contributed to the commercial
            value, sale, acquisition, or intellectual property of ARCHX AI — including
            but not limited to claims arising from any merger, acquisition, initial public
            offering, or other liquidity event — the maximum total compensation available
            to you through binding arbitration shall not exceed four dollars and fifty
            cents ($4.50) per full-arch upload submission made by you through the Portal,
            regardless of the total value of any transaction, the number of scans
            submitted, or the nature of the claim. This limitation is a material term
            of these Terms and a condition of your access to the Portal. You expressly
            waive any right to seek compensation beyond this amount for data contribution
            claims of any kind.
          </Section>

          <div style={{ marginTop: '20px', padding: '16px', background: '#1a1a1a', borderRadius: '8px', border: '1px solid #333' }}>
            <div style={{ fontSize: '12px', color: '#888' }}>
              For questions regarding these Terms, contact: <span style={{ color: '#c8922a' }}>legal@archxai.io</span>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div style={{
          padding: '20px 28px',
          borderTop: '1px solid #222',
          background: '#0d0d0d',
          borderRadius: '0 0 16px 16px',
        }}>
          {/* Checkbox */}
          <label style={{
            display: 'flex', alignItems: 'flex-start', gap: '12px',
            cursor: 'pointer', marginBottom: '20px',
          }}>
            <input
              type="checkbox"
              checked={checked}
              onChange={e => setChecked(e.target.checked)}
              style={{ marginTop: '2px', width: '16px', height: '16px', cursor: 'pointer', accentColor: '#c8922a' }}
            />
            <span style={{ fontSize: '13px', color: '#ccc', lineHeight: 1.5 }}>
              I have read, understood, and agree to the ARCHX AI Terms & Conditions,
              including the mandatory arbitration clause and limitation of liability.
            </span>
          </label>

          {/* Buttons */}
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
            <button
              onClick={handleDecline}
              disabled={declining}
              style={{
                padding: '10px 20px', borderRadius: '8px',
                border: '1px solid #444', background: 'transparent',
                color: '#888', fontWeight: '600', fontSize: '13px',
                cursor: declining ? 'not-allowed' : 'pointer',
              }}
            >
              {declining ? 'Signing out...' : 'Decline'}
            </button>
            <button
              onClick={handleAccept}
              disabled={!checked || loading}
              style={{
                padding: '10px 28px', borderRadius: '8px',
                border: 'none',
                background: checked ? 'linear-gradient(135deg, #c8922a, #e0a832)' : '#333',
                color: checked ? '#fff' : '#666',
                fontWeight: '700', fontSize: '13px',
                cursor: checked && !loading ? 'pointer' : 'not-allowed',
                transition: 'all 0.2s',
              }}
            >
              {loading ? 'Saving...' : 'Accept & Continue →'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ marginBottom: '20px' }}>
      <div style={{ fontWeight: '700', color: '#fff', marginBottom: '6px', fontSize: '13px' }}>
        {title}
      </div>
      <div>{children}</div>
    </div>
  );
}
